/**
 * Everything that can change between two `Network.update` calls, apart from the data itself.
 *
 * ⚠ This module imports NOTHING, for the same reason `fit.ts` does not: it is the pure half of a
 * decision `core.ts` makes, and `core.ts` needs a DOM, so a test could not reach it otherwise.
 */
export interface UpdateInputs {
  activeId?: string
  selectedId?: string
  query?: string
  /** The fleet itself, reduced to what can change the drawn set. See `dataSignature`. */
  dataSignature?: string
}

/**
 * The data reduced to the only thing about it that can change the drawn SET: how many rows of
 * each kind there are. A node going offline or a session starting rewrites fields on nodes that
 * are already drawn, and nothing here moves; a node joining or leaving does move it.
 *
 * ⚠⚠ WITHOUT THIS, A FLEET CHANGE THAT COINCIDES WITH THE CURSOR READS AS A HOVER. `update` is
 * debounced at 200 ms, so a node coming online while the pointer crosses the graph arrives in the
 * SAME call as the new `activeId` — every other input matches, and the real re-layout would take
 * the gentle hover restart and skip its re-frame. The fleet is then left unbalanced and the new
 * node can sit outside the window, and it stays there, because every later hover is pointer-only
 * too. Counting rows is what tells the two apart.
 */
export function dataSignature(data: MergedData): string {
  // ⚠ A node in no network is a row too. An attach adds no row of any kind and adds a link, so the
  // memberships count.
  const nodes = [...data.nodes, ...(data.noNetworkNodes ?? [])]
  const memberships = nodes.reduce((sum, node) => sum + (node.networkIds?.length ?? 0), 0)
  return [...[data.accounts, data.networks, nodes, data.services].map(rows => rows.length), memberships].join(':')
}

/**
 * Whether the only thing that changed is which node the cursor is over.
 *
 * ⚠⚠ THIS IS THE DIFFERENCE BETWEEN A SETTLE AND A RE-LAYOUT, AND THE ADAPTER CANNOT TELL THEM
 * APART. Hovering a node ADDS its leaves to the map and hovering off REMOVES them, and the
 * adapter reports both as `rerender` — the same signal a node joining the fleet or a rail switch
 * flipping produces. So a hover took the full-energy restart meant for a re-layout, and, worse,
 * re-framed the viewport: the entire picture rescaled and re-centred every time the pointer
 * crossed a node, which is the most disorienting thing the graph can do and is nobody's idea of
 * what a hover means.
 *
 * The cause is the discriminator: a leaf appearing because you pointed at something is not the
 * graph changing, it is the graph answering. Everything else here — a selection, a search — is a
 * deliberate act on the picture as a whole, and those DO deserve a re-frame.
 *
 * ⚠ The first update cannot be pointer-only: nothing was active before it, so `activeId` has not
 * changed, and the initial layout keeps its full restart and its fit.
 */
export function isPointerOnly(previous: UpdateInputs, next: UpdateInputs): boolean {
  return (
    previous.activeId !== next.activeId &&
    previous.selectedId === next.selectedId &&
    previous.query === next.query &&
    previous.dataSignature === next.dataSignature
  )
}

/**
 * Whether the pointer state names something that is no longer on the canvas (app#316).
 *
 * ⚠⚠ A HOVER USUALLY ENDS BY `mouseleave`, AND SOMETIMES BY REMOVAL — and the second one raises
 * no event to end it with. Search a name out from under a resting cursor, flip a rail switch, let
 * `Show Offline` drop the node being pointed at: d3 takes the element out of the DOM and there is
 * nothing left for the browser to fire `mouseleave` on. The state then names something that is
 * gone. Nothing looks wrong while it is gone — the adapter looks the id up and misses — and then
 * the same thing comes BACK, matches again, and is drawn lit with nothing under the cursor.
 *
 * ⚠ Chrome hides this, and only by luck. `core.ts` puts `pointer-events: none` on an element as
 * it leaves (so a fading node cannot take a hover it will never release), Chrome re-targets the
 * hover on the next frame, and a `mouseleave` arrives after all. That is a Chrome behaviour, not
 * a guarantee: a browser that re-targets only on the next pointer MOVE clears nothing. The drawn
 * set is the thing that is actually known, so it is what the question is asked of.
 *
 * ⚠ `activeId` is a MODEL id — a node id, a service id, an account id — and the nodes carry both
 * spellings (`id` and `modelNodeId`), so both go in the set.
 */
export function hoverOrphaned(nodes: GraphNode[], activeId?: string): boolean {
  if (!activeId) return false

  const drawn = new Set<string>()
  nodes.forEach(node => {
    drawn.add(node.id)
    if (node.modelNodeId) drawn.add(node.modelNodeId)
  })

  return !drawn.has(activeId)
}
